import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { 
  Gift, Sparkles, X, CheckCircle2, Trophy, Coins, Flame, 
  ChevronRight, Zap, Shield, Crown, Key, Star, Award
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function GuestBonusModal({ isOpen, onClose, onBonusClaimed }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bonusStatus, setBonusStatus] = useState(null);

  useEffect(() => {
    if (user && isOpen) {
      api.get("/wallet/bonus-status")
        .then(res => setBonusStatus(res.data))
        .catch(() => {});
    }
  }, [user, isOpen]);

  if (!isOpen) return null;

  const handleTakeBonus = async () => {
    if (!user) {
      localStorage.setItem("bonus_modal_hidden", Date.now().toString());
      if (onClose) onClose();
      navigate("/register?ref=bonus_gift");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/wallet/claim-daily-bonus");
      if (res.data?.ok) {
        toast.success(res.data.message || "Bonus revendicat cu succes! 🎉");
        if (setUser) {
          setUser(prev => prev ? { ...prev, idg_coins: (prev.idg_coins || 0) + (res.data.reward_coins || 125) } : prev);
        }
        if (onBonusClaimed) onBonusClaimed(res.data);
        localStorage.setItem("bonus_modal_hidden", Date.now().toString());
        if (onClose) onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bonusul zilnic a fost deja revendicat.");
      if (onClose) onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleRefuseBonus = () => {
    localStorage.setItem("bonus_modal_hidden", Date.now().toString());
    if (onClose) onClose();
  };

  const streak = bonusStatus?.streak || (user ? 1 : 0);
  const currentDay = Math.min(7, streak + 1);
  const rewardCoins = bonusStatus?.reward_coins || (user ? 100 + (streak * 25) : 250);

  const STREAK_DAYS = [
    { day: 1, coins: 100, isSpecial: false },
    { day: 2, coins: 125, isSpecial: false },
    { day: 3, coins: 150, isSpecial: true, perk: "VIP CS 1.6" },
    { day: 4, coins: 175, isSpecial: false },
    { day: 5, coins: 200, isSpecial: false },
    { day: 6, coins: 225, isSpecial: false },
    { day: 7, coins: 300, isSpecial: true, perk: "VIP 7 ZILE" },
  ];

  const modalContent = (
    <div 
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-lg overflow-y-auto select-none animate-fadeIn"
      onClick={(e) => { if (e.target === e.currentTarget) handleRefuseBonus(); }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
    >
      <div 
        id="guestBonusModal"
        className="relative w-full max-w-lg bg-[#080a10] border border-white/10 rounded-xl shadow-[0_30px_100px_rgba(0,0,0,0.95),0_0_40px_rgba(255,79,0,0.15)] overflow-hidden my-auto animate-scaleUp"
      >
        {/* Top Glowing Laser Border */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary,#ff4f00)] to-transparent" />
        
        {/* Atmospheric Backlights */}
        <div className="absolute -top-24 -right-24 w-56 h-56 bg-[var(--tf-primary,#ff4f00)]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-56 h-56 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button 
          onClick={handleRefuseBonus}
          className="absolute top-3.5 right-3.5 z-20 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-all cursor-pointer"
          title="Închide"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header Header Bar */}
        <div className="px-6 pt-6 pb-2 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--tf-primary,#ff4f00)] animate-ping" />
            <span>SISTEM DE RECOMPENSE CS 1.6 & COMUNITATE</span>
          </div>

          <h2 className="font-chakra font-black text-2xl sm:text-3xl uppercase tracking-wider text-white">
            {user ? "CADOU ACTIVITATE ZILNICĂ" : "PACHET DE BUN VENIT"}
          </h2>
          <p className="text-xs font-mono text-zinc-400 mt-1 max-w-sm mx-auto">
            {user 
              ? "Conectează-te în fiecare zi, menține seria activă și deblochează beneficii VIP pe serverele CS 1.6."
              : "Înregistrează-te acum și primești instant fonduri bonus pe serverele INDUNGI."}
          </p>
        </div>

        {/* Hero Reward Showcase */}
        <div className="px-6 py-4 relative z-10">
          <div className="relative p-5 rounded-xl bg-gradient-to-b from-[#0e121c] to-[#0a0c13] border border-white/10 overflow-hidden text-center shadow-inner">
            {/* Background grid pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px] pointer-events-none" />
            
            <div className="text-[10px] font-chakra font-black uppercase tracking-widest text-[var(--tf-primary,#ff4f00)]">
              RECOMPENSĂ DISPONIBILĂ ACUM
            </div>

            <div className="flex items-center justify-center gap-3 my-2">
              <div className="w-12 h-12 rounded-xl bg-[var(--tf-primary,#ff4f00)]/15 border border-[var(--tf-primary,#ff4f00)]/40 flex items-center justify-center shadow-[0_0_20px_rgba(255,79,0,0.3)]">
                <Coins className="w-7 h-7 text-[var(--tf-primary,#ff4f00)]" />
              </div>
              <div className="text-left">
                <div className="font-mono font-black text-3xl sm:text-4xl text-white tracking-tight leading-none">
                  +{rewardCoins}
                </div>
                <div className="text-[11px] font-chakra font-bold text-zinc-400 uppercase tracking-wider mt-0.5">
                  IDG COINS DIRECT ÎN BALANȚĂ
                </div>
              </div>
            </div>

            {user && (
              <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-md bg-black/50 border border-white/5 text-xs font-mono">
                <Flame className="w-3.5 h-3.5 text-orange-500 animate-pulse" />
                <span className="text-zinc-400">Serie Activă:</span>
                <strong className="text-orange-400">{streak} Zile Consecutive</strong>
              </div>
            )}
          </div>
        </div>

        {/* 7-Day Progression Cards */}
        {user && (
          <div className="px-6 pb-2 relative z-10">
            <div className="grid grid-cols-7 gap-1.5">
              {STREAK_DAYS.map(({ day, coins, isSpecial, perk }) => {
                const isClaimed = streak >= day;
                const isToday = currentDay === day && !isClaimed;

                return (
                  <div
                    key={day}
                    className={`relative p-2 rounded-lg border text-center transition-all flex flex-col justify-between min-h-[72px] ${
                      isClaimed
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                        : isToday
                          ? "bg-[var(--tf-primary,#ff4f00)]/20 border-[var(--tf-primary,#ff4f00)] text-white shadow-[0_0_15px_rgba(255,79,0,0.35)] scale-105 z-10"
                          : "bg-white/[0.02] border-white/5 text-zinc-500"
                    }`}
                  >
                    <div className="text-[9px] font-chakra font-black uppercase">
                      ZIUA {day}
                    </div>

                    <div className="my-1 flex items-center justify-center">
                      {isClaimed ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : isSpecial ? (
                        <Star className="w-4 h-4 text-amber-400 animate-pulse" />
                      ) : (
                        <Coins className="w-3.5 h-3.5 text-zinc-400" />
                      )}
                    </div>

                    <div className="text-[10px] font-mono font-bold leading-tight">
                      {isClaimed ? "GATA" : `+${coins}`}
                    </div>

                    {perk && !isClaimed && (
                      <div className="text-[7px] font-chakra font-black uppercase text-amber-400 mt-0.5 leading-none">
                        {perk}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions Footer */}
        <div className="p-6 pt-4 space-y-2 relative z-10">
          <button
            id="takeBonus"
            onClick={handleTakeBonus}
            disabled={loading}
            className="w-full py-3.5 px-6 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black bg-gradient-to-r from-[var(--tf-primary,#ff4f00)] to-amber-500 hover:brightness-110 active:scale-[0.99] transition-all shadow-[0_0_25px_rgba(255,79,0,0.4)] flex items-center justify-center gap-2 group cursor-pointer"
          >
            <Zap className="w-4 h-4 fill-black" />
            <span>{user ? "REVENDICĂ RECOMPENSA ZILNICĂ" : "CREEAZĂ CONT & PRIMEȘTE CADOU"}</span>
            <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          <button
            id="refuseBonus"
            onClick={handleRefuseBonus}
            className="w-full py-2 text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer"
          >
            // Închide fereastra
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
